import { take, takeLatest, put, call, fork, select, race, delay } from 'redux-saga/effects';
import callApi from 'utils/api';
import takeLeading from 'utils/effect-creators';
import * as actionTypes from './message-center-constants';

import {
  fetchActionRequiredMessages,
  actionRequiredMessagesSuccess,
  actionRequiredMessagesFailure,
  historySuccess,
  historyFailure,
  dismissSuccess,
  dismissFailure,
  dismissAllSuccess,
  dismissAllFailure,
  getHistoryDataNextSuccess,
  getHistoryDataNextFailure,
  actionRequiredCountSuccess,
  actionRequiredCountFailure,
  actionUpdatedMessagesSuccess,
  actionUpdatedMessagesFailure,
  fetchActionRequiredCount,
} from './message-center-actions';
import { connectionStates } from '../../../utils/app-configs';

const getConnection = state => state.sockets.connection;

export function* fetchActionRequiredMessagesRequest(action) {
  const isMessageCenterOpen = yield select(state => state.systemMessage.messageCenterToggled);
  try {
    const results = yield call(callApi, {
      endpoint: `messages/actionRequired${isMessageCenterOpen ? '' : '/count'}`,
      options: {
        errorMessage: 'Could not fetch messages',
        retryAction: action,
      },
    });
    if (isMessageCenterOpen) {
      yield put(actionRequiredMessagesSuccess(results));
    } else {
      yield put(actionRequiredCountSuccess(results));
    }
  } catch (err) {
    if (isMessageCenterOpen) {
      yield put(actionRequiredMessagesFailure(err));
    } else {
      yield put(actionRequiredCountFailure(err));
    }
  }
}

export function* fetchActionUpdatedMessagesRequest(action) {
  const isMessageCenterOpen = yield select(state => state.systemMessage.messageCenterToggled);
  const endpoint = isMessageCenterOpen ?
    'messages/correlation' :
    'messages/actionRequired/count';

  const options = isMessageCenterOpen ?
    {
      method: 'POST',
      body: JSON.stringify(action.payload.correlationIds),
      errorMessage: 'Could not fetch messages',
      retryAction: action,
    } :
    {
      errorMessage: 'Could not fetch count',
      retryAction: action,
    };

  try {
    const results = yield call(callApi, {
      endpoint,
      options,
    });
    if (isMessageCenterOpen) {
      yield put(actionUpdatedMessagesSuccess(results));
      yield put(fetchActionRequiredCount());
    } else {
      yield put(actionRequiredCountSuccess(results));
    }
  } catch (err) {
    if (isMessageCenterOpen) {
      yield put(actionUpdatedMessagesFailure(err));
    } else {
      yield put(actionRequiredCountFailure(err));
    }
  }
}

export function* fetchActionRequiredCountRequest(action) {
  try {
    const results = yield call(callApi, {
      endpoint: 'messages/actionRequired/count',
      options: {
        errorMessage: 'Could not fetch message count',
        retryAction: action,
      },
    });
    yield put(actionRequiredCountSuccess(results));
  } catch (err) {
    yield put(actionRequiredCountFailure(err));
  }
}

export function* fetchHistory(action) {
  try {
    const results = yield call(callApi, {
      endpoint: `messages/history?pageSize=10&page=${action.payload.page || 1}`,
      options: {
        errorMessage: 'Could not fetch history',
        retryAction: action,
      },
    });
    yield put(historySuccess(results));
  } catch (err) {
    yield put(historyFailure(err));
  }
}

export function* dismissMessage(action) {
  try {
    const results = yield call(callApi, {
      endpoint: `messages/dismiss/${action.payload.id}`,
      options: {
        errorMessage: 'Could not dismiss message',
        retryAction: action,
      },
    });
    yield put(dismissSuccess(results));
    const connection = yield select(getConnection);
    if (connection?.state !== connectionStates.Connected) {
      yield put(fetchActionRequiredMessages());
    }
  } catch (err) {
    yield put(dismissFailure(err));
  }
}

export function* dismissAllMessages(action) {
  try {
    const results = yield call(callApi, {
      endpoint: 'messages/dismissAll',
      options: {
        errorMessage: 'Could not dismiss all messages',
        retryAction: action,
      },
    });
    yield put(dismissAllSuccess(results));
    const connection = yield select(getConnection);
    if (connection?.state !== connectionStates.Connected) {
      yield put(fetchActionRequiredMessages());
    }
  } catch (err) {
    yield put(dismissAllFailure(err));
  }
}

export function* doGetHistoryDataNext(action) {
  try {
    const results = yield call(callApi, {
      endpoint: `messages/history?pageSize=10&page=${action.payload.page || 1}`,
      options: {
        errorMessage: 'Could not get history',
        retryAction: action,
      },
    });
    yield put(getHistoryDataNextSuccess(results));
  } catch (err) {
    yield put(getHistoryDataNextFailure(err));
  }
}

const getDelay = state => state.messageCenter.pollingDelay;
export function* pollMessages() {
  yield take(actionTypes.START_POLLING);
  let connection = yield select(getConnection);
  let pollDelay = yield select(getDelay);
  while (connection?.state !== connectionStates.Connected && pollDelay) {
    yield put(fetchActionRequiredMessages());
    connection = yield select(getConnection);
    pollDelay = yield select(getDelay);
    yield race([
      delay(pollDelay),
      take(actionTypes.SET_FAST_POLLING_REQUEST),
      take(actionTypes.SET_SLOW_POLLING_REQUEST),
    ]);
  }
}

function* watchFetchActionRequiredMessages() {
  yield takeLeading(
    actionTypes.ACTION_REQUIRED_MESSAGES_REQUEST,
    fetchActionRequiredMessagesRequest,
  );
}

function* watchFetchActionUpdatedMessages() {
  yield takeLatest(
    actionTypes.ACTION_UPDATED_MESSAGES_REQUEST,
    fetchActionUpdatedMessagesRequest,
  );
}


function* watchFetchActionRequiredCount() {
  yield takeLatest(
    actionTypes.ACTION_REQUIRED_COUNT_REQUEST,
    fetchActionRequiredCountRequest,
  );
}

function* watchFetchHistory() {
  yield takeLeading(actionTypes.HISTORY_REQUEST, fetchHistory);
}

function* watchDismissMessage() {
  yield takeLatest(actionTypes.DISMISS_REQUEST, dismissMessage);
}

function* watchDismissAllMessages() {
  yield takeLatest(actionTypes.DISMISS_ALL_REQUEST, dismissAllMessages);
}

function* watchdoGetHistoryNext() {
  yield takeLatest(actionTypes.GET_HISTORY_DATA_NEXT_REQUEST, doGetHistoryDataNext);
}

export default [
  fork(watchFetchActionRequiredMessages),
  fork(watchFetchActionRequiredCount),
  fork(watchFetchActionUpdatedMessages),
  fork(watchFetchHistory),
  fork(watchDismissMessage),
  fork(pollMessages),
  fork(watchDismissAllMessages),
  fork(watchdoGetHistoryNext),
];
